// 登录/注册/重置密码浮层 / Auth modal (login/register/reset views).
// 本文件负责：DOM 渲染、tab 切换、右上角关闭、表单清空、以及表单提交的
// 业务逻辑（登录 / 注册两步 / 重置两步）。
// Responsibilities: DOM rendering, tab switching, top-right close, form reset,
// and form-submission business logic (login, two-stage register/reset).

import { authApi as defaultAuthApi } from "@/api/auth";
import { startButtonLoading } from "@/features/async-button";
import { writeCachedAuthStatus } from "@/features/auth-session-cache";
import { runPendingAction } from "@/features/pending-action";
import { state } from "@/state/store";

export type AuthTab = "login" | "register";

// 注入依赖：测试里传 mock authApi / Injected deps so tests can stub authApi.
export interface Deps {
  authApi: Pick<
    typeof defaultAuthApi,
    "login" | "registerRequest" | "register" | "passwordResetRequest" | "passwordReset"
  >;
}
const DEFAULT_DEPS: Deps = { authApi: defaultAuthApi };

// 当前绑定的依赖（由 openAuthModal 写入，_enterResetView 重新渲染时复用）。
// The deps currently bound to the modal — reused when _enterResetView re-renders.
let _currentDeps: Deps = DEFAULT_DEPS;

export function openAuthModal(
  tab: AuthTab = "login",
  deps: Partial<Deps> = {},
): void {
  const root = document.getElementById("auth-modal");
  if (!root) throw new Error("#auth-modal mount point missing");

  _currentDeps = { ...DEFAULT_DEPS, ...deps };

  root.innerHTML = _renderShell(tab);
  root.hidden = false;

  root.querySelector(".close-btn")?.addEventListener("click", closeAuthModal);

  root.querySelectorAll<HTMLElement>("[data-tab]").forEach((el) => {
    el.addEventListener("click", () => _switchTab(el.dataset.tab as AuthTab));
  });

  _bindLoginSubmit(_currentDeps);
  _bindForgotLink();
  _bindRegisterStage1(_currentDeps);
  _bindRegisterStage2(_currentDeps);
}

export function closeAuthModal(): void {
  const root = document.getElementById("auth-modal");
  if (!root) return;
  root.querySelectorAll<HTMLInputElement>("input").forEach((i) => (i.value = ""));
  root.innerHTML = "";
  root.hidden = true;
}

function _switchTab(tab: AuthTab): void {
  const root = document.getElementById("auth-modal");
  if (!root) return;
  root.querySelectorAll(".tab-login, .tab-register").forEach((el) =>
    el.classList.remove("active"),
  );
  root.querySelector(`.tab-${tab}`)?.classList.add("active");
  root.querySelectorAll(".pane").forEach((el) => el.classList.add("hidden"));
  root.querySelector(`.pane-${tab}`)?.classList.remove("hidden");
}

// 登录提交：成功后 runPendingAction → reload / Login submit: run pending, then reload.
function _bindLoginSubmit(d: Deps): void {
  const form = document.querySelector<HTMLFormElement>("#auth-modal .login-form");
  if (!form) return;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (form.dataset.busy === "1") return;
    const err = form.querySelector<HTMLElement>(".err")!;
    const button = form.querySelector<HTMLButtonElement>("button[type=submit]")!;
    err.hidden = true;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value
      .trim()
      .toLowerCase();
    const password = (form.elements.namedItem("password") as HTMLInputElement).value;
    const remember = (form.elements.namedItem("remember") as HTMLInputElement).checked;
    const restore = startButtonLoading(button, "Signing in");
    form.dataset.busy = "1";
    try {
      const res = await d.authApi.login({ email, password, remember });
      // 登录成功后刷新 store.auth 到新 AuthStatus 形状。
      // On login success, refresh store.auth using the new AuthStatus shape.
      const nextAuth = {
        authenticated: true,
        provider: "local",
        user: res.user ?? null,
        capabilities: {
          register: true,
          password_reset: true,
          change_password: true,
          logout: true,
        },
      };
      state.auth = nextAuth;
      writeCachedAuthStatus(nextAuth);
      closeAuthModal();
      await runPendingAction();
      window.location.reload();
    } catch (e: unknown) {
      err.hidden = false;
      const apiErr = e as { code?: string; message?: string };
      err.textContent =
        apiErr.code === "LOGIN_FAILED"
          ? "Incorrect email or password"
          : apiErr.message || "Sign-in failed. Please try again.";
    } finally {
      delete form.dataset.busy;
      restore();
    }
  });
}

// 忘记密码链接 → 切到重置视图 / Forgot-password link → switch to reset view.
function _bindForgotLink(): void {
  const link = document.querySelector<HTMLAnchorElement>("#auth-modal .forgot");
  link?.addEventListener("click", (ev) => {
    ev.preventDefault();
    _enterResetView();
  });
}

// 注册第一步：请求 OTP → 翻到 stage 2 / Register stage 1: request OTP, flip to stage 2.
function _bindRegisterStage1(d: Deps): void {
  const form = document.querySelector<HTMLFormElement>("#auth-modal .register-form");
  if (!form) return;
  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (form.dataset.stage !== "1") return;
    if (form.dataset.busy === "1") return;
    const err = form.querySelector<HTMLElement>(".err")!;
    const btn = form.querySelector<HTMLButtonElement>(".btn-send-otp")!;
    err.hidden = true;
    const email = (form.elements.namedItem("email") as HTMLInputElement).value
      .trim()
      .toLowerCase();
    const restore = startButtonLoading(btn, "Sending");
    form.dataset.busy = "1";
    try {
      await d.authApi.registerRequest({ email });
      form.dataset.stage = "2";
      form.querySelector<HTMLElement>(".stage2")!.hidden = false;
      btn.hidden = true;
      (form.elements.namedItem("email") as HTMLInputElement).readOnly = true;
    } catch (e: unknown) {
      err.hidden = false;
      const ex = e as { code?: string; name?: string; retryAfterSeconds?: number; message?: string };
      if (ex.code === "EMAIL_EXISTS") err.textContent = "This email is already registered. Please sign in.";
      else if (ex.name === "RateLimitedError")
        err.textContent = `Please try again in ${ex.retryAfterSeconds} seconds.`;
      else if (ex.code === "EMAIL_INVALID") err.textContent = "Enter a valid email address.";
      else err.textContent = ex.message || "Request failed. Please try again.";
    } finally {
      delete form.dataset.busy;
      restore();
    }
  });
}

// 注册第二步：OTP + 密码 → /register；OTP_LOCKED 时回到 stage 1。
// Register stage 2: submit OTP + password; roll back to stage 1 if OTP_LOCKED.
function _bindRegisterStage2(d: Deps): void {
  const form = document.querySelector<HTMLFormElement>("#auth-modal .register-form");
  if (!form) return;
  const btn = form.querySelector<HTMLButtonElement>(".btn-submit-register");
  btn?.addEventListener("click", async () => {
    if (form.dataset.busy === "1") return;
    const err = form.querySelector<HTMLElement>(".err")!;
    err.hidden = true;
    const otp = (form.elements.namedItem("otp") as HTMLInputElement).value.trim();
    const password = (form.elements.namedItem("password") as HTMLInputElement).value;
    const confirm = (form.elements.namedItem("confirm") as HTMLInputElement).value;
    if (!/^\d{6}$/.test(otp)) {
      err.hidden = false;
      err.textContent = "Verification code must be 6 digits.";
      return;
    }
    if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(password)) {
      err.hidden = false;
      err.textContent = "Password must be at least 8 characters and include letters and numbers.";
      return;
    }
    if (password !== confirm) {
      err.hidden = false;
      err.textContent = "Passwords do not match.";
      return;
    }
    const restore = startButtonLoading(btn, "Creating account");
    form.dataset.busy = "1";
    try {
      const res = await d.authApi.register({
        email: (form.elements.namedItem("email") as HTMLInputElement).value
          .trim()
          .toLowerCase(),
        otp,
        password,
      });
      const nextAuth = {
        authenticated: true,
        provider: "local",
        user: res.user ?? null,
        capabilities: {
          register: true,
          password_reset: true,
          change_password: true,
          logout: true,
        },
      };
      state.auth = nextAuth;
      writeCachedAuthStatus(nextAuth);
      closeAuthModal();
      window.location.reload();
    } catch (e: unknown) {
      err.hidden = false;
      const ex = e as { code?: string; message?: string };
      if (ex.code === "OTP_LOCKED") {
        err.textContent = "This verification code has expired. Request a new one.";
        form.dataset.stage = "1";
        form.querySelector<HTMLElement>(".stage2")!.hidden = true;
        form.querySelector<HTMLButtonElement>(".btn-send-otp")!.hidden = false;
        (form.elements.namedItem("email") as HTMLInputElement).readOnly = false;
      } else if (ex.code === "OTP_INVALID") {
        err.textContent = "Incorrect verification code.";
      } else if (ex.code === "EMAIL_EXISTS") {
        err.textContent = "This email is already registered. Please sign in.";
      } else if (ex.code === "PASSWORD_TOO_WEAK") {
        err.textContent = "Password must be at least 8 characters and include letters and numbers.";
      } else {
        err.textContent = ex.message || "Registration failed.";
      }
    } finally {
      delete form.dataset.busy;
      restore();
    }
  });
}

// 重置密码视图：替换整个 modal 外壳 / Reset-password view: swap the modal shell.
function _enterResetView(): void {
  const root = document.getElementById("auth-modal");
  if (!root) return;
  root.innerHTML = `
    <div class="overlay" aria-hidden="true"></div>
    <div class="modal" role="dialog" aria-modal="true">
      <button class="close-btn" aria-label="Close">×</button>
      <a href="#" class="back-to-login">← Back to sign in</a>
      <div class="reset-view">
        <h3>Reset password</h3>
        <form class="reset-form" data-stage="1">
          <input name="email" type="email" placeholder="Email" required />
          <button type="submit" class="btn-send-reset-otp">Get code</button>
          <div class="stage2" hidden>
            <input name="otp" type="text" placeholder="Verification code" maxlength="6" />
            <input name="password" type="password" placeholder="New password" />
            <input name="confirm" type="password" placeholder="Confirm new password" />
            <button type="button" class="btn-submit-reset">Reset password</button>
          </div>
          <div class="err" hidden></div>
        </form>
      </div>
    </div>
  `;
  root.querySelector(".close-btn")?.addEventListener("click", closeAuthModal);
  root.querySelector(".back-to-login")?.addEventListener("click", (ev) => {
    ev.preventDefault();
    openAuthModal("login");
  });
  _bindResetSubmit(_currentDeps);
}

// 重置表单的两段式提交 / Two-stage submit binding for the reset form.
function _bindResetSubmit(d: Deps): void {
  const form = document.querySelector<HTMLFormElement>("#auth-modal .reset-form");
  if (!form) return;

  form.addEventListener("submit", async (ev) => {
    ev.preventDefault();
    if (form.dataset.stage !== "1") return;
    if (form.dataset.busy === "1") return;
    const err = form.querySelector<HTMLElement>(".err")!;
    const btn = form.querySelector<HTMLButtonElement>(".btn-send-reset-otp")!;
    err.hidden = true;
    const restore = startButtonLoading(btn, "Sending");
    form.dataset.busy = "1";
    try {
      await d.authApi.passwordResetRequest({
        email: (form.elements.namedItem("email") as HTMLInputElement).value
          .trim()
          .toLowerCase(),
      });
      form.dataset.stage = "2";
      form.querySelector<HTMLElement>(".stage2")!.hidden = false;
    } catch (e: unknown) {
      err.hidden = false;
      const ex = e as { name?: string; retryAfterSeconds?: number; message?: string };
      err.textContent =
        ex.name === "RateLimitedError"
          ? `Please try again in ${ex.retryAfterSeconds} seconds.`
          : ex.message || "Request failed.";
    } finally {
      delete form.dataset.busy;
      restore();
    }
  });

  form
    .querySelector<HTMLButtonElement>(".btn-submit-reset")
    ?.addEventListener("click", async () => {
      if (form.dataset.busy === "1") return;
      const err = form.querySelector<HTMLElement>(".err")!;
      const btn = form.querySelector<HTMLButtonElement>(".btn-submit-reset")!;
      err.hidden = true;
      const otp = (form.elements.namedItem("otp") as HTMLInputElement).value.trim();
      const password = (form.elements.namedItem("password") as HTMLInputElement)
        .value;
      const confirm = (form.elements.namedItem("confirm") as HTMLInputElement).value;
      if (!/^\d{6}$/.test(otp)) {
        err.hidden = false;
        err.textContent = "Verification code must be 6 digits.";
        return;
      }
      if (!/^(?=.*[A-Za-z])(?=.*\d).{8,}$/.test(password)) {
        err.hidden = false;
        err.textContent = "Password must be at least 8 characters and include letters and numbers.";
        return;
      }
      if (password !== confirm) {
        err.hidden = false;
        err.textContent = "Passwords do not match.";
        return;
      }
      const restore = startButtonLoading(btn, "Resetting");
      form.dataset.busy = "1";
      try {
        await d.authApi.passwordReset({
          email: (form.elements.namedItem("email") as HTMLInputElement).value
            .trim()
            .toLowerCase(),
          otp,
          new_password: password,
        });
        // 成功后回到登录 tab，顶部浮一个成功提示。
        // On success, return to the login tab and show a success toast.
        openAuthModal("login");
        const shell = document.querySelector("#auth-modal .modal");
        shell?.insertAdjacentHTML(
          "afterbegin",
          `<div class="success-toast">Password reset. Sign in with your new password.</div>`,
        );
      } catch (e: unknown) {
        err.hidden = false;
        const ex = e as { code?: string; message?: string };
        err.textContent =
          ex.code === "OTP_LOCKED"
            ? "This verification code has expired. Request a new one."
            : ex.code === "OTP_INVALID"
              ? "Incorrect verification code."
              : ex.message || "Password reset failed.";
      } finally {
        delete form.dataset.busy;
        restore();
      }
    });
}

function _renderShell(tab: AuthTab): string {
  return `
    <div class="overlay" aria-hidden="true"></div>
    <div class="modal" role="dialog" aria-modal="true">
      <button class="close-btn" aria-label="Close">×</button>
      <div class="tabs">
        <button class="tab-login ${tab === "login" ? "active" : ""}" data-tab="login">Sign in</button>
        <button class="tab-register ${tab === "register" ? "active" : ""}" data-tab="register">Create account</button>
      </div>
      <div class="pane pane-login ${tab === "login" ? "" : "hidden"}">
        <form class="login-form">
          <input name="email" type="email" placeholder="Email" required />
          <input name="password" type="password" placeholder="Password" required />
          <label class="remember">
            <input type="checkbox" name="remember" /> Keep me signed in for 7 days
          </label>
          <button type="submit">Sign in</button>
          <a href="#" class="forgot">Forgot password?</a>
          <div class="err" hidden></div>
        </form>
      </div>
      <div class="pane pane-register ${tab === "register" ? "" : "hidden"}">
        <form class="register-form" data-stage="1">
          <input name="email" type="email" placeholder="Email" required />
          <button type="submit" class="btn-send-otp">Get code</button>
          <div class="stage2" hidden>
            <input name="otp" type="text" placeholder="Verification code" maxlength="6" />
            <input name="password" type="password" placeholder="Set password" />
            <input name="confirm" type="password" placeholder="Confirm password" />
            <button type="button" class="btn-submit-register">Create account</button>
          </div>
          <div class="err" hidden></div>
        </form>
      </div>
    </div>
  `;
}
